import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineL, HeadlineM } from '@sorare/core/src/atoms/typography';
import { NewsTitleContent } from '@sorare/core/src/components/player/News/NewsTitleContent';
import { NewsUpdates } from '@sorare/core/src/components/player/News/NewsUpdates';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { playerDetails } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import { AveragePlayerScores } from '@sorare/marketplace/src/components/player/AveragePlayerScores';
import { PerformanceBlocks } from '@sorare/marketplace/src/components/player/PerformanceBlocks';
import { FilterKey } from '@sorare/marketplace/src/lib/performance';
import { LazyFixtureChart } from '@sorare/marketplace/src/shared/stats/LastScores/LazyFixtureChart';
import { usePerformanceFilters } from '@sorare/marketplace/src/shared/stats/hooks/usePerformanceFilters';

import { usePerformanceFiltersOptions } from 'hooks/usePerformanceFiltersOptions';

import { AveragePlayerScoreSwitch } from './AveragePlayerScoreSwitch';
import type {
  PerformanceBlocksQuery,
  PerformanceBlocksQueryVariables,
  Performance_anyPlayer,
} from './__generated__/index.graphql';

const NewsBlock = styled(Vertical)`
  border: 1px solid var(--c-nd-200);
  border-radius: var(--double-unit);
  padding: var(--double-unit);
`;

const PERFORMANCE_BLOCK_QUERY = gql`
  query PerformanceBlocksQuery(
    $playerSlug: String!
    $position: Position!
    $competitionSlug: String
    $location: AverageLastScoreLocation
    $span: AverageLastScoreSpan!
    $teamSlug: String
  ) {
    anyPlayer(slug: $playerSlug) {
      slug
      ...PerformanceBlocks_anyPlayer
      ...AveragePlayerScores_anyPlayer
    }
  }
  ${AveragePlayerScores.fragments.anyPlayer}
  ${PerformanceBlocks.fragments.anyPlayer}
` as TypedDocumentNode<PerformanceBlocksQuery, PerformanceBlocksQueryVariables>;

type Props = {
  player: Performance_anyPlayer;
};

export const Performance = ({ player }: Props) => {
  const {
    flags: { useFilterOptionsFromMembership = false },
  } = useFeatureFlags();
  const { filters, handleFilterChange, gqlFilters } =
    usePerformanceFilters(player);
  const options = usePerformanceFiltersOptions({
    player,
  });

  const position = filters.position || player.anyPositions[0];
  const { data, loading } = useQuery(PERFORMANCE_BLOCK_QUERY, {
    variables: {
      playerSlug: player.slug,
      position,
      competitionSlug: gqlFilters.competitionSlug,
      span: gqlFilters.span,
      teamSlug: gqlFilters.teamSlug,
      location: gqlFilters.location,
    },
  });

  const { anyPlayer } = data || {};
  const isPlayerType = isType(anyPlayer, 'Player');

  return (
    <Vertical gap={2}>
      <AveragePlayerScores player={player} />
      <HeadlineL as="h2">
        <FormattedMessage {...playerDetails.breakdown} />
      </HeadlineL>
      <LazyFixtureChart
        playerSlug={player.slug}
        filters={filters}
        onFilterChange={handleFilterChange}
        {...(useFilterOptionsFromMembership && { options })}
        hideTitle
        stickyFilters
      />
      {!!isPlayerType && (
        <>
          <AveragePlayerScoreSwitch
            averagePlayerScore={filters[FilterKey.MODE]}
            onChange={value => handleFilterChange(FilterKey.MODE, value)}
          />
          <PerformanceBlocks
            player={anyPlayer}
            filters={filters}
            loading={loading}
          />
        </>
      )}
      {!isPlayerType && (
        <NewsBlock gap={1.5}>
          <HeadlineM as="h2">
            <NewsTitleContent />
          </HeadlineM>
          <NewsUpdates updates={player.news} playerName={player.displayName} />
        </NewsBlock>
      )}
    </Vertical>
  );
};

Performance.fragments = {
  anyPlayer: gql`
    fragment Performance_anyPlayer on AnyPlayerInterface {
      slug
      displayName
      anyPositions
      news(last: 10) {
        ...NewsUpdates_PlayerNewsUpdate
      }
      ...AveragePlayerScores_anyPlayer
      ...usePerformanceFiltersOptions_anyPlayer
      ...usePerformanceFilters_anyPlayer
    }
    ${usePerformanceFiltersOptions.fragments.anyPlayer}
    ${AveragePlayerScores.fragments.anyPlayer}
    ${usePerformanceFilters.fragments.anyPlayer}
    ${NewsUpdates.fragments.PlayerNewsUpdate}
  ` as TypedDocumentNode<Performance_anyPlayer>,
};
